import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit2, FolderOpen, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminCategoriesPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("Admin") || (user?.roles || []).includes("admin");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", status: "Active" });

  const load = async () => {
    try { const { data } = await api.get("/admin/categories"); setCategories(data); }
    catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "", status: "Active" }); setShowDialog(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || "", status: c.status || "Active" }); setShowDialog(true); };

  const save = async () => {
    try {
      if (editing) { await api.put(`/admin/categories/${editing.category_id}`, form); toast.success("Category updated"); }
      else { await api.post("/admin/categories", form); toast.success("Category created"); }
      setShowDialog(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Categories</h1>
          <p className="text-sm text-zinc-500 mt-1">Product categories used across the catalogue</p>
        </div>
        {isAdmin && <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-category-btn"><Plus size={14} /> Add Category</Button>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="categories-grid">
        {loading ? <p className="text-xs text-zinc-400 col-span-4 text-center py-8">Loading...</p> :
        categories.length === 0 ? <p className="text-xs text-zinc-400 col-span-4 text-center py-8">No categories. Create your first category to get started.</p> :
        categories.map(c => (
          <div key={c.category_id} className="border border-zinc-200 p-4 hover:border-zinc-400 transition-colors" data-testid={`category-card-${c.category_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="w-9 h-9 bg-zinc-100 flex items-center justify-center rounded-sm"><FolderOpen size={16} className="text-zinc-500" /></div>
              <div className="flex items-center gap-1">
                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${c.status === "Active" ? "status-active" : "status-inactive"}`}>{c.status}</span>
                {isAdmin && <button onClick={() => openEdit(c)} className="p-1 hover:bg-zinc-100 rounded-sm"><Edit2 size={12} className="text-zinc-400" /></button>}
                {isAdmin && <button onClick={async () => { if (!window.confirm(`Delete category "${c.name}"?`)) return; try { await api.delete(`/admin/categories/${c.category_id}`); toast.success("Category deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } }} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={12} className="text-red-400" /></button>}
              </div>
            </div>
            <h3 className="font-heading font-semibold text-sm text-zinc-900">{c.name}</h3>
            <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{c.category_id}</p>
            {c.description && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{c.description}</p>}
            <p className="text-[10px] text-zinc-400 mt-2">{c.product_count || 0} products</p>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Category" : "New Category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Category Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="e.g. Ice Pop, Muffin, Snack" className="mt-1 rounded-sm" data-testid="category-name-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" rows={2} /></div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={save} className="rounded-sm text-xs" data-testid="save-category-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
